import { Fragment } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import type { TFunction } from 'i18next';
import type {
  ClaimClosingItem,
  ClaimClosingRow,
} from '@/utils/approval/claimClosing';

/** 저장 HTML 버튼 — 레거시 문서와 같은 class·data 이름이라 조회 변환을 같이 씀 */
export const DETAIL_BUTTON_CLASS = 'btn-detail-popup';
export const DELETE_BUTTON_CLASS = 'btn-detail-hide';
export const REJECT_BUTTON_CLASS = 'btn-detail-reject';
export const CARD_CLASS = 'cc-card';

const COL_WIDTHS = [
  '10%',
  '12%',
  '10%',
  '10%',
  '10%',
  '10%',
  '15%',
  '10%',
  '13%',
];

export const text = (v: unknown) =>
  v === null || v === undefined ? '' : String(v).trim();

export const toNumber = (v: unknown) => Number(text(v).replace(/,/g, '')) || 0;

/** 레거시 number_format. 응답에 키가 없으면 빈칸, 값이 비면 0 */
export const formatNumber = (v: unknown, decimals = 0) => {
  if (v === undefined) return '';
  const raw = text(v);
  if (!raw) return (0).toFixed(decimals);
  const n = Number(raw.replace(/,/g, ''));
  return Number.isFinite(n)
    ? n.toLocaleString('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })
    : raw;
};

/** 레거시 appr_sub_view_forms_return_tax_mstr — 송장마다 머리글·값 두 단 */
const RowBlock = ({ row, t }: { row: ClaimClosingRow; t: TFunction }) => (
  <>
    <tr>
      <th className="text-center bg-gray" rowSpan={2}>
        {t('브랜드')}
      </th>
      <th className="text-center bg-gray" rowSpan={2}>
        {t('청구번호')}
      </th>
      <th className="text-center bg-gray" rowSpan={2}>
        {t('청구일자')}
      </th>
      <th className="text-center bg-gray" rowSpan={2}>
        {t('결재일자')}
      </th>
      <th className="text-center bg-gray" rowSpan={2}>
        {t('발주구분')}
      </th>
      <th className="text-center bg-gray" rowSpan={2} colSpan={2}>
        {t('마감형태')}
      </th>
      <th className="text-center bg-gray" colSpan={2}>
        {t('업체')}
      </th>
    </tr>
    <tr>
      <th className="text-center bg-gray">{t('코드')}</th>
      <th className="text-center bg-gray">{t('업체명')}</th>
    </tr>
    <tr>
      <td className="text-center">{text(row.brandKor)}</td>
      <td className="text-center">{text(row.invNo)}</td>
      <td className="text-center">{text(row.reqYmd)}</td>
      <td className="text-center">{text(row.settlementYmd)}</td>
      <td className="text-center">{text(row.grTypKor)}</td>
      <td className="text-center" colSpan={2}>
        {text(row.supplyKor)}
      </td>
      <td className="text-center">{text(row.vdCd)}</td>
      <td className="text-center">{text(row.vdNm)}</td>
    </tr>
    <tr>
      <th className="text-center bg-gray" colSpan={2}>
        {t('결제금액')}
      </th>
      <th className="text-center bg-gray">{t('부가세')}</th>
      <th className="text-center bg-gray" colSpan={2}>
        {t('공급가액')}
      </th>
      <th className="text-center bg-gray" colSpan={2}>
        {t('계산서 비발행금액')}
      </th>
      <th className="text-center bg-gray" colSpan={2}>
        {t('CLAIM NOTE 총 금액')}
      </th>
    </tr>
    <tr>
      <td className="text-right" colSpan={2}>
        {formatNumber(row.totInvAmt)}
      </td>
      <td className="text-right">{formatNumber(row.vatAmt)}</td>
      <td className="text-right" colSpan={2}>
        {formatNumber(row.supplyAmt)}
      </td>
      {/* API 가 이미 화면용 음수로 줌 — 부호를 다시 바꾸지 않음 */}
      <td className="text-right" colSpan={2}>
        {formatNumber(row.claimSapAmtKrw)}
      </td>
      <td className="text-right" colSpan={2}>
        {formatNumber(row.claimAmtKrw)}
      </td>
    </tr>
  </>
);

/** 건마다 카드 하나 */
const Card = ({ item, t }: { item: ClaimClosingItem; t: TFunction }) => (
  <div
    className={CARD_CLASS}
    data-item-id={item.aprvId}
    data-doc-tit={item.docTit}
  >
    <table>
      <colgroup>
        {COL_WIDTHS.map((width, i) => (
          <col key={i} width={width} />
        ))}
      </colgroup>
      <tbody>
        <tr>
          <th className="text-center card-title" colSpan={7}>
            ({item.docTit})
          </th>
          <th className="text-right card-action" colSpan={2}>
            <button
              type="button"
              className={DETAIL_BUTTON_CLASS}
              data-aprv_id={item.aprvId}
              data-sys_id="FNF"
            >
              {t('상세보기')}
            </button>
            <button
              type="button"
              className={DELETE_BUTTON_CLASS}
              data-aprv_id={item.aprvId}
              data-aprv_doctit={item.docTit}
            >
              {t('삭제')}
            </button>
            <button
              type="button"
              className={REJECT_BUTTON_CLASS}
              data-aprv_id={item.aprvId}
              data-aprv_doctit={item.docTit}
            />
          </th>
        </tr>
        {item.rows.map((row, i) => (
          <Fragment key={i}>
            <RowBlock row={row} t={t} />
          </Fragment>
        ))}
      </tbody>
    </table>
  </div>
);

/** 저장 본문 — 고른 순서대로 카드 */
export const buildClaimClosingHtml = (
  items: ClaimClosingItem[],
  t: TFunction,
): string =>
  items.length
    ? renderToStaticMarkup(
        <>
          {items.map((item) => (
            <Card key={item.aprvId} item={item} t={t} />
          ))}
        </>,
      )
    : '';
